/**
 * Runs `worker` over `items` with at most `limit` of them in flight at once.
 *
 * `beforeEach` is awaited before a lane picks up its next item. Return `false`
 * from it to stop the run — already in-flight work still settles — or simply
 * await inside it to pause without losing the queue.
 *
 * Resolves once every lane has drained. A `worker` that throws is logged and
 * skipped — the run must never tear down half-way with lanes still running
 * unobserved, because the caller's bookkeeping runs after this resolves.
 *
 * @param {Array} items
 * @param {number} limit
 * @param {(item: any, index: number) => Promise<void>} worker
 * @param {() => Promise<boolean>} [beforeEach]
 */
export default async function runWithConcurrency(items, limit, worker, beforeEach) {
  if (!Array.isArray(items) || !items.length) return;

  let cursor = 0;

  const lanes = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      while (cursor < items.length) {
        if (beforeEach && (await beforeEach()) === false) return;
        // Re-check after the await: other lanes may have drained the queue
        // while this one was paused.
        if (cursor >= items.length) return;
        const index = cursor;
        cursor += 1;
        try {
          await worker(items[index], index);
        } catch (error) {
          console.error("runWithConcurrency: worker threw", error);
        }
      }
    },
  );

  await Promise.all(lanes);
}
